/* Common module for inline-editable table for spare parts */

import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import RcPartModelSelect from '@/components/rc/common/RcPartModelSelect.vue'
import RcSelectPartType from '@/components/rc/common/RcSelectPartType.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import RcSparePartCustomerSelect from '@/components/rc/common/RcSparePartCustomerSelect.vue'
import type { ManagedRow } from '@/composables/table-row-inline-edit'
import router from '@/router'
import { partIcons, PartType, PartTypesShortNames } from '@/types/rc/miners/PartType'
import { SparePartStatus, type ISparePart } from '@/types/rc/spare-parts'
import { formatCurrency } from '@/util/formatters'
import { generateModelDisplayName } from '@/util/rc'
import { Icon } from '@iconify/vue'
import { ElButton, ElInput, ElLink, ElSelect, ElOption, ElTooltip } from 'element-plus'
import type { CellRenderer } from 'element-plus/es/components/table-v2/src/types.mjs'
import { h, type Ref, type VNode } from 'vue'

// Defines the structure of a cell renderer, one component for editing and one for display
// Each editing renderer deals with the `rowBeingEdited` buffer, which is always the row base type
// Each display renderer deals with a single model prop, and displays its single proper datum
export interface EditableCellRendererFields<TRow, TData> {
  editing?: (rowBeingEdited: Ref<TRow>) => VNode
  display: (cellData: TData) => VNode
}

export type EditableCellRenderer<TRow = unknown, TData = unknown> = (
  options: SparesTableRenderingOptions
) => EditableCellRendererFields<TRow, TData>

// A spare part's table row
type ISparePartTableRow = ManagedRow<ISparePart> | undefined

export const idRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['id']> = () => ({
  editing: (rowBeingEdited) => h('p', {}, rowBeingEdited.value?.id),
  display: (cellData) => h('p', {}, cellData),
})

export const typeRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['part_type']> = ({ compact }) => ({
  editing: (rowBeingEdited) =>
    h(RcSelectPartType, {
      shortNames: compact,
      modelValue: rowBeingEdited.value?.part_type,
      'onUpdate:modelValue': (val: any) => {
        if (!rowBeingEdited.value) return
        rowBeingEdited.value.part_type = val

        // Reset model when type changes
        if (val !== rowBeingEdited.value.initialValue?.part_type) {
          rowBeingEdited.value.part_model = null
          rowBeingEdited.value.part_model_version = null
        }
      },
    }),
  display: (cellData) => {
    const partType = cellData as PartType
    return h('span', { class: 'part-type-cell' }, [
      h(Icon, { icon: partIcons[partType] }),
      compact ? PartTypesShortNames[partType] : cellData,
    ])
  },
})

export const modelRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['part_model']> = () => ({
  editing: (rowBeingEdited) =>
    h(RcPartModelSelect, {
      partType: rowBeingEdited.value?.part_type,
      modelValue: {
        name: rowBeingEdited.value?.part_model ?? null,
        version: rowBeingEdited.value?.part_model_version ?? null,
      },
      'onUpdate:modelValue': (val) => {
        if (!rowBeingEdited.value) return
        if (val) {
          rowBeingEdited.value.part_model = val.name
          rowBeingEdited.value.part_model_version = val.version
        } else {
          rowBeingEdited.value.part_model = null
          rowBeingEdited.value.part_model_version = null
        }
      },
    }),
  display: (cellData) => h('p', {}, cellData ?? '-'),
})

export const snRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['part_sn']> = () => ({
  editing: (rowBeingEdited) =>
    h(ElInput, {
      modelValue: rowBeingEdited.value?.part_sn,
      'onUpdate:modelValue': (val) => {
        if (!rowBeingEdited.value) return
        rowBeingEdited.value.part_sn = val
      },
    }),
  display: (cellData) => {
    if (!cellData) return h('p', {}, '-')
    return h('p', {}, cellData || '-')
  },
})

export const statusRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['status']> = ({ refurbished }) => ({
  editing: (rowBeingEdited) =>
    h(
      ElSelect,
      {
        modelValue: rowBeingEdited.value?.status,
        'onUpdate:modelValue': (val: SparePartStatus) => {
          if (!rowBeingEdited.value) return
          rowBeingEdited.value.status = val
        },
        placeholder: 'Select status',
        style: 'width: 100%',
      },
      () =>
        Object.values(SparePartStatus).map((status) =>
          h(ElOption, {
            key: status,
            label: status,
            value: status,
          })
        )
    ),
  display: (cellData) =>
    h('div', { class: 'flex items-center gap-2' }, [
      h(StatusBadge, { status: cellData }, () => cellData),
      refurbished
        ? h(
            ElTooltip,
            { content: 'Refurbished', effect: 'dark', placement: 'top' },
            {
              default: () =>
                h(Icon, {
                  icon: 'material-symbols:build',
                  class: 'text-lg cursor-help',
                  height: 16,
                }),
            }
          )
        : null,
    ]),
})
export const minerSerialNumberRenderer: EditableCellRenderer<
  ISparePartTableRow,
  ISparePart['miner_coming_from'] | ISparePart['miner_used_on']
> = (options) => ({
  display: (cellData) => {
    if (!cellData) return h('p', {}, '-')

    // If isCustomer is true, display just the serial number without link
    if (options?.isCustomer) {
      return h('p', {}, cellData.sn || '-')
    }

    // Otherwise, display as clickable link
    return (
      cellData &&
      h(
        ElLink,
        {
          style: { color: 'var(--text-secondary)' },
          onClick: () => {
            router.push(`/rc/miners/${cellData.id}`)
          },
        },
        () => cellData.sn || '-'
      )
    )
  },
})

export const woLinkRenderer: EditableCellRenderer<
  ISparePartTableRow,
  ISparePart['miner_coming_from'] | ISparePart['miner_used_on']
> = () => ({
  display: (cellData) =>
    h(
      ElLink,
      {
        style: { color: 'var(--text-secondary)' },
        onClick: () => {
          router.push(`work-orders/${cellData}`)
        },
      },
      () => cellData || '-'
    ),
})

export const priceRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['price']> = () => ({
  editing: (rowBeingEdited) =>
    h(ElCurrencyInput, {
      modelValue: rowBeingEdited.value?.price,
      'onUpdate:modelValue': (val: number | null) => {
        if (!rowBeingEdited.value) return
        rowBeingEdited.value.price = val
      },
    }),
  display: (cellData) => {
    if (!cellData) return h('p', {}, `-`)
    try {
      return h('p', {}, formatCurrency(cellData))
    } catch {
      return h('p', {}, `NaN`)
    }
  },
})

export const ownerRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['customer']> = () => ({
  editing: (rowBeingEdited) =>
    h(RcSparePartCustomerSelect, {
      modelValue: rowBeingEdited.value?.customer,
      'onUpdate:modelValue': (val: any) => {
        if (!rowBeingEdited.value) return
        rowBeingEdited.value.customer.id = val.id!
        rowBeingEdited.value.customer.name = val.name
      },
    }),
  display: (cellData) => h('p', {}, cellData.name),
})

export const siteRenderer: EditableCellRenderer<ISparePartTableRow, ISparePart['site']> = () => ({
  editing: (rowBeingEdited) =>
    h(RcSiteSelect, {
      modelValue: rowBeingEdited.value?.site ?? {},
      'onUpdate:modelValue': (val: any) => {
        if (!rowBeingEdited.value) return
        rowBeingEdited.value.site.site_id = val.site_id!
        rowBeingEdited.value.site.site_name = val.site_name
      },
    }),
  display: (cellData) => h('p', {}, cellData.site_name),
})

export const refurbishRenderer: EditableCellRenderer<ISparePartTableRow, number | null> = ({ customMutation }) => ({
  display: (cellData) =>
    cellData
      ? // If id, render a button
        h(
          ElButton,
          {
            link: true,
            title: 'Refurbish',
            onClick: () => {
              return DeleteConfirmationModal(
                'Refurbish Spare Part',
                'Are you sure you want to mark this spare part as refurbished? This action cannot be undone',
                { id: cellData },
                customMutation.mutateAsync,
                () => {},
                'Refurbish'
              )
            },
          },
          () => [h(Icon, { icon: 'material-symbols:build', height: '16' })]
        )
      : // Else, an empty div
        h('div'),
})

interface SparesTableRenderingOptions {
  compact?: boolean
  customMutation?: any
  refurbished?: boolean
  isCustomer?: boolean
}

type EditableTableRenderer = (
  rowBeingEdited: Ref<ManagedRow<ISparePart> | undefined>,
  options?: SparesTableRenderingOptions
) => CellRenderer<ManagedRow<ISparePart>>

// Pipes datum and options to proper renderer, having access to the full row scope (to handle special cases and such)
export const sparePartsTableCellRendererGenerator: EditableTableRenderer =
  (rowBeingEditedRef, options) =>
  ({ rowData, column }) => {
    const columnKey = <string>column.key
    const cellData = rowData[columnKey]
    const editing = rowBeingEditedRef.value?.id === rowData.id

    const renderers: Record<string, EditableCellRenderer<ISparePartTableRow, any>> = {
      id: idRenderer,
      part_type: typeRenderer,
      part_model: modelRenderer,
      part_sn: snRenderer,
      price: priceRenderer,
      customer: ownerRenderer,
      status: statusRenderer,
      miner_used_on: minerSerialNumberRenderer,
      miner_coming_from: minerSerialNumberRenderer,
      source_work_order: woLinkRenderer,
      site: siteRenderer,
      refurbish: refurbishRenderer,
    }

    // Update options for some special cases
    const customOptions = options ?? {}
    if (column.key == 'status') customOptions.refurbished = rowData['refurbished']

    const cellRenderer = renderers[columnKey](customOptions)

    // Special cases:
    let displayData = cellData
    if (column.key == 'part_model')
      displayData = generateModelDisplayName(rowData.part_model, rowData.part_model_version)
    if (column.key == 'customer' && cellData.id == 0) displayData = { ...cellData, name: 'Owned by us' }

    // For the refurbish column, compute `id` or `null` depending on status and customer
    if (column.key == 'refurbish')
      displayData = rowData.status == SparePartStatus.Broken && rowData.customer.id == 0 ? rowData.id : null

    return editing && cellRenderer.editing ? cellRenderer.editing(rowBeingEditedRef) : cellRenderer.display(displayData)
  }
